import { collisionGrid, storyMap, storyNpcsOf } from '@rdd/content/story'
import { STORY_CHALLENGE_RANGE_TILES, STORY_EMOTE_INTERVAL_MS, type StoryNpc } from '@rdd/contracts'
import { z } from 'zod'
import { readConfig } from './config'
import { colyseusTransport } from './net/colyseus-transport'
import type { Snapshot } from './net/protocol'
import { WorldConnection } from './net/world-connection'
import type { Me } from './session/session'
import { Session } from './session/session'
import { t } from './i18n/pt-BR'
import { challengePanel, closedText } from './ui/challenge-panel'
import { EMOTE_GLYPHS } from './ui/emotes'
import { npcDialog } from './ui/npc-dialog'
import { hud } from './ui/hud'
import { deniedScreen, failedScreen, loadingScreen, refusedScreen, show } from './ui/screens'
import { RemotePlayers } from './world/remote-players'
import { WorldScene } from './world/scene'

/**
 * A partida do cliente do mundo. docs/21, ADR 0042.
 *
 * Sem sessao, o jogador vai ao login do `web2` -- a porta de entrada do jogo inteiro. Depois do login ele cai
 * no lobby do `web2`, e volta ao mundo pelo item do menu: a volta automatica fica fora da v1, porque exigiria
 * mudar o fluxo de login do `web2` (modo_historia/03#HM-002).
 */

const root = document.getElementById('app')
const config = readConfig(import.meta.env)
const session = new Session(config.apiUrl)

async function start(): Promise<void> {
  if (root === null) return
  show(root, loadingScreen())
  const outcome = await session.boot()
  switch (outcome.kind) {
    case 'anonymous':
      window.location.assign(`${config.web2Url}/login`)
      return
    case 'denied':
      show(root, deniedScreen(config.web2Url))
      return
    case 'failed':
      show(
        root,
        failedScreen(() => void start()),
      )
      return
    case 'ready':
      await enterWorld(root, outcome.me)
      return
  }
}

/**
 * O mundo compartilhado (modo_historia/04#HM-001): pede a entrada ao `api`, entra na sala, e so desenha o mapa
 * quando a primeira foto da sala chega -- e ela que diz onde o personagem nasceu. O servidor de mundo e a
 * autoridade da posicao; o cliente so preve o proprio passo.
 */
async function enterWorld(root: HTMLElement, me: Me): Promise<void> {
  let scene: WorldScene | null = null
  let stage: HTMLElement | null = null
  const panel = challengePanel()
  const dialog = npcDialog()
  const remotes = new RemotePlayers(me.userId)
  let resolveFirst: (snapshot: Snapshot) => void = () => undefined
  const firstSnapshot = new Promise<Snapshot>((resolve) => (resolveFirst = resolve))

  const connection = new WorldConnection(session.api, colyseusTransport, {
    onSnapshot(snapshot) {
      remotes.reset(snapshot.players)
      const self = snapshot.players.find((player) => player.id === snapshot.you)
      if (self !== undefined) scene?.walker.place(self.x, self.y)
      resolveFirst(snapshot)
    },
    onDelta(delta) {
      remotes.upsert(delta.upserts)
      remotes.remove(delta.removed)
      const self = delta.upserts.find((player) => player.id === me.userId)
      if (self !== undefined) scene?.authoritative(self.x, self.y)
    },
    onEmote(emote) {
      scene?.showEmote(emote.playerId, EMOTE_GLYPHS[emote.emoteId], STORY_EMOTE_INTERVAL_MS)
    },
    onChallenged(challenge) {
      panel.incoming(challenge.from.nick, challenge.expiresAt, (accept) => {
        connection.answer(challenge.challengeId, accept)
        panel.notice(accept ? t.challenge.starting : t.challenge.closed.DECLINED)
      })
    },
    onChallengePending(challenge) {
      panel.pending(challenge.to.nick, challenge.expiresAt)
    },
    onChallengeClosed(closed) {
      panel.notice(closedText(closed.reason, closed.about, me.userId))
    },
    onDuelReady(duelId) {
      // A posicao ja foi reportada pela sala no aceite: o cliente so sai e vai ao duelo, no `web2`
      // (docs/18 secao 41.2). A volta e pelo `returnTo=story`.
      connection.leave()
      window.location.assign(`${config.web2Url}/duel/${encodeURIComponent(duelId)}?returnTo=story`)
    },
    onNpcDuelClosed(reason) {
      const known = t.npc.closed as Record<string, string>
      dialog.notice(known[reason] ?? t.npc.closed.UNAVAILABLE)
    },
    onTravel() {
      // A sala ja saiu (`WorldConnection`): a tela escurece, a cena velha some, e a entrada se refaz -- o `api`
      // ja gravou a posicao no destino, e a entrada cai nele (docs/21 secao 5).
      const old = scene
      scene = null
      stage?.classList.add('leaving')
      setTimeout(() => {
        old?.destroy()
        void enterWorld(root, me)
      }, TRAVEL_FADE_MS)
    },
    onLost(reason) {
      scene?.destroy()
      scene = null
      show(root, reason === 'forbidden' ? deniedScreen(config.web2Url) : failedScreen(() => void start()))
    },
  })

  const failure = await connection.connect()
  if (failure !== null) {
    show(
      root,
      failure === 'forbidden'
        ? deniedScreen(config.web2Url)
        : failure === 'unavailable'
          ? failedScreen(() => void start())
          : refusedScreen(config.web2Url, failure.refused),
    )
    return
  }
  /** A conversa com o NPC (docs/21 secoes 12 e 13): com bot, ela oferece o duelo. */
  const talkTo = (npc: StoryNpc): void => {
    dialog.open(npc, portraitOf(npc), npc.botId === undefined ? null : () => connection.npcDuel(npc.id))
  }

  const snapshot = await firstSnapshot
  const self = snapshot.players.find((player) => player.id === snapshot.you)
  // O mapa e o da sala em que o `api` pos o jogador (docs/21 secao 9), e nao um fixo do cliente.
  const map = storyMap(snapshot.mapId)
  const grid = collisionGrid(snapshot.mapId)
  if (map === undefined || grid === undefined) {
    connection.leave()
    show(
      root,
      failedScreen(() => void start()),
    )
    return
  }

  stage = document.createElement('main')
  stage.className = 'world'
  show(root, stage)
  scene = new WorldScene({
    mapId: snapshot.mapId,
    map,
    grid,
    spawn: self === undefined ? { x: 0, y: 0 } : { x: self.x, y: self.y },
    nickname: me.nickname ?? '',
    character: self?.character ?? null,
    selfId: me.userId,
    assetsBase: import.meta.env.BASE_URL,
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    onStep: (dir) => connection.sendStep(dir),
    onSelectPlayer: (id, nick, distance) =>
      panel.target(nick, distance <= STORY_CHALLENGE_RANGE_TILES, () => connection.challenge(id)),
    onNearNpc: (npc) => dialog.reach(npc, talkTo),
    onTalk: (npc) => talkTo(npc),
  })
  scene.remotes = () => remotes.views()
  scene.advanceRemotes = (deltaMs) => remotes.advance(deltaMs)
  await scene.mount(stage)
  // A volta do duelo contra o NPC (docs/21 secao 13): ele comenta o resultado, se esta neste mapa.
  const outcome = connection.npcOutcome
  const opponent =
    outcome === null ? undefined : storyNpcsOf(snapshot.mapId).find((placed) => placed.npc.botId === outcome.botId)
  const line =
    opponent === undefined || outcome === null ? undefined : outcome.won ? opponent.npc.lines.lose : opponent.npc.lines.win
  if (opponent !== undefined && line !== undefined) dialog.speak(opponent.npc, portraitOf(opponent.npc), line)
  stage.append(
    hud(
      config.web2Url,
      () => void leaveWorld(connection),
      (id) => connection.sendEmote(id),
    ),
    panel.element,
    dialog.element,
  )
}

/**
 * "Voltar ao jogo" (docs/21 secao 4): sai da sala, encerra a sessao no `api` e so entao vai ao `web2`. Se o
 * `api` nao responder, o jogador vai mesmo assim -- o prazo da sessao a encerra.
 */
async function leaveWorld(connection: WorldConnection): Promise<void> {
  connection.leave()
  await Promise.race([
    session.api.request('/story/world/leave', z.unknown(), { method: 'POST', body: {}, keepalive: true }),
    new Promise((resolve) => setTimeout(resolve, LEAVE_WAIT_MS)),
  ])
  window.location.assign(`${config.web2Url}/`)
}

/** O retrato do NPC na caixa de dialogo, pelo id dele. */
const portraitOf = (npc: StoryNpc): string => `${import.meta.env.BASE_URL}story/npcs/${npc.id}/portrait.webp`

/** O escurecimento da passagem: o mesmo tempo do `.world.leaving` do CSS. */
const TRAVEL_FADE_MS = 300

/** Quanto a saida espera o `api` antes de navegar de qualquer jeito. */
const LEAVE_WAIT_MS = 1500

void start()
